import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { api } from "@/lib/api";
import useRedirectFallback from "@/lib/useRedirectFallback";
import ProductCard from "@/components/ProductCard";
import Seo, { articleSchema, breadcrumbSchema } from "@/components/Seo";
import EditOnSiteLink from "@/components/EditOnSiteLink";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  // When the article 404s, consult the redirect map before showing 404 UI.
  useRedirectFallback(Boolean(error));

  useEffect(() => {
    setArticle(null);
    setError("");
    api
      .get(`/articles/${slug}`)
      .then(async (r) => {
        setArticle(r.data);
        const slugs = r.data.related_product_slugs || [];
        if (slugs.length) {
          const results = await Promise.all(
            slugs.map((s) => api.get(`/products/${s}`).then((x) => (x.data.available === false ? null : x.data)).catch(() => null))
          );
          setRelated(results.filter(Boolean));
        }
      })
      .catch(() => setError("Article not found."));
  }, [slug]);

  const seoSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        articleSchema(article),
        breadcrumbSchema([
          { name: "Home", url: `${window.location.origin}/` },
          { name: "Articles", url: `${window.location.origin}/articles` },
          { name: article.title, url: `${window.location.origin}/article/${article.slug}` },
        ]),
      ],
    };
  }, [article]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20">
        <div className="brut bg-white p-8 text-center">
          <div className="font-display text-4xl mb-2">404</div>
          <p className="mb-4">{error}</p>
          <Link
            to="/articles"
            className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press"
          >
            All articles
          </Link>
        </div>
      </div>
    );

  if (!article)
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 font-mono uppercase text-xs tracking-widest opacity-70">
        Loading...
      </div>
    );

  const date = new Date(article.created_at).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div data-testid={`article-page-${article.slug}`}>
      <Seo
        title={article.title}
        description={article.excerpt || article.title}
        path={`/article/${article.slug}`}
        image={article.cover_image}
        type="article"
        schema={seoSchema}
      />
      {/* Hero */}
      <section className="border-b-2 border-black">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="font-mono uppercase text-[11px] tracking-widest mb-3">
            <Link to="/articles" className="hover:pcs-text-pink">
              ← All articles
            </Link>
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            {article.title}
          </h1>
          <div className="mt-4 font-mono uppercase text-[11px] tracking-widest text-gray-700 flex items-center gap-3 flex-wrap">
            <span>Published {date}</span>
            <EditOnSiteLink type="article" slug={article.slug} />
          </div>
          {article.cover_image && (
            <div className="mt-8 brut bg-white overflow-hidden">
              <img
                src={article.cover_image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        <div className="brut bg-white px-4 py-3 mb-8 text-sm" data-testid="article-affiliate-disclosure">
          <b>Heads up:</b> this post contains affiliate links. If you buy through them, PartyCostumeShop.com may earn a
          commission at no extra cost to you. As an Amazon Associate we earn from qualifying purchases.{" "}
          <Link to="/page/affiliate-disclosure" className="underline hover:pcs-text-pink">Learn more</Link>
        </div>
        {article.excerpt && (
          <p className="font-display text-2xl md:text-3xl tracking-tight leading-snug mb-8 text-gray-900">
            {article.excerpt}
          </p>
        )}
        <article className="prose-pcs" data-testid="article-body">
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
            {article.content_md || ""}
          </ReactMarkdown>
        </article>
      </section>

      {/* Related products */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 border-t-2 border-black">
          <div className="font-mono uppercase text-[11px] tracking-widest pcs-text-pink mb-2">
            Mentioned in this article
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-none mb-8">
            Shop the picks.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
